import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip,
  Slider,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { useHelpContent } from '../../utils/HelpUtils';

export type SuggestTreeImportMode = 'category_tree' | 'content_only';

interface SuggestTreeContextDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: (
    context: string,
    maxDepth: number,
    treeStrategy: 'top_down' | 'bottom_up',
    importMode: SuggestTreeImportMode,
    charsPerSegment: number,
  ) => void;
  fileNames: string[];
}

const DEFAULT_CHARS_PER_SEGMENT = 600;
const MIN_CHARS_PER_SEGMENT = 100;
const MAX_CHARS_PER_SEGMENT = 20000;

const SuggestTreeContextDialog: React.FC<SuggestTreeContextDialogProps> = ({
  open,
  onClose,
  onProceed,
  fileNames,
}) => {
  const [context, setContext] = useState('');
  const [maxDepth, setMaxDepth] = useState(1);
  const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>('top_down');
  const [importMode, setImportMode] = useState<SuggestTreeImportMode>('category_tree');
  const [charsPerSegment, setCharsPerSegment] = useState<number>(DEFAULT_CHARS_PER_SEGMENT);
  const { getTooltipTextForState } = useHelpContent();

  const clampChars = (value: number) =>
    Math.max(MIN_CHARS_PER_SEGMENT, Math.min(MAX_CHARS_PER_SEGMENT, value));

  const handleProceed = () => {
    onProceed(context.trim(), maxDepth, treeStrategy, importMode, clampChars(charsPerSegment));
  };

  const handleClose = () => {
    setContext('');
    setMaxDepth(1);
    setTreeStrategy('top_down');
    setImportMode('category_tree');
    setCharsPerSegment(DEFAULT_CHARS_PER_SEGMENT);
    onClose();
  };

  const isProceedDisabled = () => {
    return false; // Allow proceeding even without context
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>New document from files</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {fileNames.map((fileName, index) => (
              <Chip key={index} label={fileName} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">Import mode</FormLabel>
            <RadioGroup
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as SuggestTreeImportMode)}
            >
              <FormControlLabel
                value="category_tree"
                control={<Radio />}
                label="Build category structure (AI names folders)"
              />
              <FormControlLabel
                value="content_only"
                control={<Radio />}
                label="Content only under document root (group segments, no folders)"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Content only still clusters similar segments and merges small consecutive pieces; it skips
              creating category names.
            </Typography>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            type="number"
            label="Characters per section"
            value={charsPerSegment}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              setCharsPerSegment(Number.isNaN(parsed) ? DEFAULT_CHARS_PER_SEGMENT : parsed);
            }}
            onBlur={() => setCharsPerSegment((prev) => clampChars(prev))}
            inputProps={{ min: MIN_CHARS_PER_SEGMENT, max: MAX_CHARS_PER_SEGMENT, step: 100 }}
            variant="outlined"
            size="small"
            helperText={`Approximate size of each content section. Consecutive segments are merged up to this many characters before a new section starts (default ${DEFAULT_CHARS_PER_SEGMENT}). Larger values produce fewer, longer sections.`}
            sx={{ width: 260 }}
          />
        </Box>

        <Typography variant="body1" gutterBottom>
          <strong>Optional Context:</strong>{' '}
          {importMode === 'content_only'
            ? 'Optional hints for segmentation (domain, interview type, etc.).'
            : 'Provide additional context to help the AI generate better category names and clustering.'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Examples of helpful context:
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Domain/Field:</strong> "This is a legal document about contract law"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Purpose:</strong> "These are research papers for a literature review"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Structure Preference:</strong> "Organize by chapters, then by topics"
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            • <strong>Key Themes:</strong> "Focus on technical specifications and implementation details"
          </Typography>
        </Box>

        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Context (Optional)"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Enter any additional context that might help with categorization..."
            helperText="This context will be provided to the AI to improve category naming and clustering"
            variant="outlined"
            sx={{ mb: 3 }}
          />
        </Tooltip>

        {importMode === 'category_tree' && (
        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">
              <Tooltip title={getTooltipTextForState('suggest-tree-strategy', true)} placement="top">
                <span>Building strategy</span>
              </Tooltip>
            </FormLabel>
            <RadioGroup
              row
              value={treeStrategy}
              onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
            >
              <FormControlLabel
                value="top_down"
                control={<Radio />}
                label="Top-down (fewer broad categories first)"
              />
              <FormControlLabel
                value="bottom_up"
                control={<Radio />}
                label="Bottom-up (finer categories, then group upward)"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Top-down matches the classic suggester. Bottom-up favors more leaf buckets before merging into parents.
            </Typography>
          </FormControl>
        </Box>
        )}

        {importMode === 'category_tree' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Maximum Tree Depth: {maxDepth}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the maximum number of levels deep the suggested tree can be. Higher values create more detailed hierarchies.
          </Typography>
          <Tooltip title={getTooltipTextForState('suggest-tree-max-depth', true)} placement="top">
            <Slider
              value={maxDepth}
              onChange={(_, value) => setMaxDepth(value as number)}
              min={1}
              max={6}
              step={1}
              marks
              valueLabelDisplay="auto"
              sx={{ mt: 2 }}
            />
          </Tooltip>
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={handleClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('suggest-tree-proceed', true)} placement="top">
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            disabled={isProceedDisabled()}
          >
            {importMode === 'content_only' ? 'Prepare content' : 'Generate preview'}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SuggestTreeContextDialog; 